import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Landmark, Link2, LockKeyhole, RefreshCw, ShieldCheck, Trash2, Upload, FileSpreadsheet, CircleAlert } from "lucide-react";

const API = process.env.REACT_APP_API || "";

const countryLabels = { DE: "Deutschland", AT: "Österreich", CH: "Schweiz" };
const statusLabels = {
  prepared_customer_contract_required: "Vorbereitet – eigener Anbieter-Vertrag erforderlich",
  prepared: "Vorbereitet",
};

export function BankingConnectorView() {
  const [catalog, setCatalog] = useState([]);
  const [connections, setConnections] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [providerId, setProviderId] = useState("finapi_customer_owned");
  const [country, setCountry] = useState("DE");
  const [label, setLabel] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [csvFile, setCsvFile] = useState(null);
  const inputRef = useRef(null);

  const headers = useCallback(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogResponse, connectionsResponse, batchResponse] = await Promise.all([
        axios.get(`${API}/api/banking-connectors/catalog`, { headers: headers() }),
        axios.get(`${API}/api/banking-connectors/connections`, { headers: headers() }),
        axios.get(`${API}/api/banking-connectors/csv/batches`, { headers: headers() }),
      ]);
      setCatalog(catalogResponse.data.providers || []);
      setConnections(connectionsResponse.data || []);
      setBatches(batchResponse.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Der Banking-Bereich konnte gerade nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const providerOptions = useMemo(() => catalog.filter((provider) => provider.id !== "csv_import"), [catalog]);
  const selectedProvider = providerOptions.find((provider) => provider.id === providerId);
  const validCountries = useMemo(() => selectedProvider?.countries || ["DE", "AT", "CH"], [selectedProvider]);

  useEffect(() => {
    if (!validCountries.includes(country)) {
      const fallback = validCountries[0] || "DE";
      setCountry(fallback);
      setCurrency(fallback === "CH" ? "CHF" : "EUR");
    }
  }, [country, validCountries]);

  const requestProviderPreparation = async () => {
    if (!selectedProvider) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/banking-connectors/connections/request`, {
        provider_id: providerId,
        country,
        label: label.trim(),
      }, { headers: headers() });
      setLabel("");
      toast.success("Die eigene Provider-Anbindung wurde als Vorbereitung gespeichert.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Die Vorbereitung konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const importCsv = async () => {
    if (!csvFile) {
      toast.error("Bitte wähle zuerst eine CSV-Datei aus.");
      return;
    }
    if (!csvFile.name.toLowerCase().endsWith(".csv")) {
      toast.error("Bitte wähle eine CSV-Datei aus.");
      return;
    }
    setSaving(true);
    try {
      const data = new FormData();
      data.append("file", csvFile);
      data.append("currency", currency);
      const response = await axios.post(`${API}/api/banking-connectors/csv/import`, data, {
        headers: headers(),
      });
      const result = response.data;
      toast.success(`${result.imported_count} Umsatzzeile(n) importiert${result.duplicate_count ? `, ${result.duplicate_count} Duplikat(e) übersprungen` : ""}.`);
      setCsvFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Der CSV-Import ist fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const removeConnection = async (id) => {
    if (!window.confirm("Diese vorbereitete Provider-Anbindung löschen? Es sind darin keine Zugangsdaten gespeichert.")) return;
    try {
      await axios.delete(`${API}/api/banking-connectors/connections/${id}`, { headers: headers() });
      toast.success("Vorbereitung gelöscht.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Die Vorbereitung konnte nicht gelöscht werden.");
    }
  };

  const removeBatch = async (batch) => {
    if (!window.confirm(`Import „${batch.original_filename}" löschen? Dadurch werden ${batch.imported_count} importierte Umsatzzeile(n) dieser Datei entfernt. Bereits manuell zugeordnete Rechnungen werden nicht verändert.`)) return;
    try {
      await axios.delete(`${API}/api/banking-connectors/csv/batches/${batch.id}`, { headers: headers() });
      toast.success("Import und die zugehörigen Umsatzzeilen wurden gelöscht.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Der Import konnte nicht gelöscht werden.");
    }
  };

  if (loading) return <p className="p-4 text-sm text-gray-400">Banking Connector Center wird geladen …</p>;

  return (
    <div className="space-y-6" data-testid="banking-connector-center">
      <div>
        <h2 className="flex items-center gap-2 text-xl font-bold"><Landmark className="h-5 w-5 text-[var(--brand-primary)]" /> Bankanbindung</h2>
        <p className="mt-1 text-sm text-[var(--brand-muted)]">Optional für deine Organisation. Ohne Bankanbindung kannst du das Studio vollständig nutzen.</p>
      </div>

      <Card className="border-[var(--brand-border)] bg-[var(--brand-bg)] p-4">
        <div className="flex gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[var(--brand-primary)]" />
          <div className="text-sm text-[var(--brand-ink)]">
            <p className="font-semibold">Deine Daten bleiben in deiner Organisation.</p>
            <p className="mt-1 text-[var(--brand-muted)]">Bank-Login, PIN und TAN werden hier nie abgefragt. CSV-Originaldateien werden nach dem Einlesen nicht gespeichert. Bei einer späteren echten Provider-Verbindung werden technische Zugangsdaten ausschließlich verschlüsselt und nur für deine Organisation verarbeitet.</p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="p-5">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="mt-0.5 h-5 w-5 text-[var(--brand-primary)]" />
            <div>
              <h3 className="font-semibold text-[var(--brand-ink)]">CSV-Umsätze importieren</h3>
              <p className="mt-1 text-sm text-[var(--brand-muted)]">Lade eine CSV-Datei aus deiner eigenen Bank hoch. Die Originaldatei wird nicht gespeichert; importiert werden nur die Umsatzzeilen in deiner Organisation.</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="mb-2 block">Währung der Datei</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="CHF">CHF</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">CSV-Datei</Label>
              <Input ref={inputRef} type="file" accept=".csv,text/csv" onChange={(event) => setCsvFile(event.target.files?.[0] || null)} />
            </div>
          </div>
          <Button className="mt-4 bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-dark)]" onClick={importCsv} disabled={saving}>
            <Upload className="mr-2 h-4 w-4" /> CSV sicher importieren
          </Button>
          <p className="mt-3 text-xs text-[var(--brand-muted)]">Unterstützte Spalten heißen beispielsweise Datum, Betrag, Empfänger/Auftraggeber, Verwendungszweck und IBAN. Duplikate werden anhand der Umsatzdaten übersprungen.</p>
        </Card>

        <Card className="p-5">
          <div className="flex items-start gap-3">
            <Link2 className="mt-0.5 h-5 w-5 text-[var(--brand-primary)]" />
            <div>
              <h3 className="font-semibold text-[var(--brand-ink)]">Eigene Bankanbieter-Anbindung vorbereiten</h3>
              <p className="mt-1 text-sm text-[var(--brand-muted)]">Nur für Kunden mit einem eigenen Vertrag bei einem später unterstützten Anbieter. Hier werden noch keine API-Schlüssel oder Bankdaten eingegeben.</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="mb-2 block">Eigener Anbieter</Label>
              <Select value={providerId} onValueChange={setProviderId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {providerOptions.map((provider) => <SelectItem key={provider.id} value={provider.id}>{provider.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Land</Label>
              <Select value={country} onValueChange={(value) => { setCountry(value); setCurrency(value === "CH" ? "CHF" : "EUR"); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {validCountries.map((code) => <SelectItem key={code} value={code}>{countryLabels[code]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3">
            <Label className="mb-2 block">Eigene Bezeichnung (optional)</Label>
            <Input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="z. B. Geschäftskonto Hauptbank" maxLength={120} />
          </div>
          <Button variant="outline" className="mt-4" onClick={requestProviderPreparation} disabled={saving || !selectedProvider}>
            <LockKeyhole className="mr-2 h-4 w-4" /> Eigene Anbindung vorbereiten
          </Button>
          {selectedProvider && <p className="mt-3 text-xs text-[var(--brand-muted)]">{selectedProvider.description}</p>}
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="p-5">
          <h3 className="font-semibold text-[var(--brand-ink)]">Vorbereitete eigene Anbindungen</h3>
          <div className="mt-3 space-y-2">
            {connections.length === 0 ? <p className="text-sm text-[var(--brand-muted)]">Noch keine eigene Provider-Anbindung vorbereitet.</p> : connections.map((connection) => (
              <div key={connection.id} className="flex items-center justify-between gap-3 rounded-lg border border-[var(--brand-border)] p-3">
                <div className="min-w-0">
                  <p className="font-medium text-[var(--brand-ink)]">{connection.label}</p>
                  <p className="text-xs text-[var(--brand-muted)]">{connection.provider_label} · {countryLabels[connection.country] || connection.country}</p>
                  <p className="mt-1 text-xs text-amber-700">{statusLabels[connection.status] || connection.status}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => removeConnection(connection.id)} aria-label="Vorbereitung löschen"><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-[var(--brand-ink)]">CSV-Importverlauf</h3>
          <div className="mt-3 space-y-2">
            {batches.length === 0 ? <p className="text-sm text-[var(--brand-muted)]">Noch keine CSV-Datei importiert.</p> : batches.map((batch) => (
              <div key={batch.id} className="flex items-center justify-between gap-3 rounded-lg border border-[var(--brand-border)] p-3">
                <div className="min-w-0">
                  <p className="truncate font-medium text-[var(--brand-ink)]">{batch.original_filename}</p>
                  <p className="text-xs text-[var(--brand-muted)]">{batch.imported_count} importiert · {batch.duplicate_count} Duplikate · {batch.currency} · {batch.created_at?.slice(0, 10)}</p>
                  <p className="mt-1 text-xs text-green-700">Originaldatei nicht gespeichert</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => removeBatch(batch)} aria-label="CSV-Import löschen"><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="flex gap-2"><CircleAlert className="mt-0.5 h-4 w-4 shrink-0" /><p><strong>Keine automatische Bankaktion:</strong> Ein Import führt weder zu einer Buchung noch zu einer Zahlung oder Mahnung. Umsatz-Zuordnungen werden später immer nur vorgeschlagen und bewusst bestätigt.</p></div>
      </Card>
    </div>
  );
}
